import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Alert,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { Platform } from 'react-native';
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy as fbOrderBy,
  limit as fbLimit,
  doc,
  getDoc,
  setDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
// SDK NATIVO de Firestore. En Android el SDK web va SIN SESIÓN (el login vive
// en @react-native-firebase vía authBridge) y las reglas de /food_orders y
// /rides exigen isSignedIn() + que el uid coincida. Resultado en el teléfono:
// el cliente veía "Aún no has hecho ningún pedido" recién pedido, y el chofer
// veía RD$0 en su Panel de Ganancias después de trabajar toda la semana.
// Mismo patrón que services/firestoreService.ts.
import nativeFirestore from '@react-native-firebase/firestore';
import { useThemeColors, SIZES } from '../../constants/theme';
import { useAppStore } from '../../store/useAppStore';
import { useFoodStore } from '../../store/useFoodStore';
import { db } from '../../services/firebaseConfig';
import { esInvitado } from '../../services/modoInvitado';
import { pesos } from '../../utils/dinero';

/**
 * TOPE DE VIAJES QUE LEE EL PANEL DE GANANCIAS DEL CHOFER.
 *
 * Está aquí, como constante, porque la consulta y el RÓTULO tienen que decir el
 * mismo número. Mientras el chofer tenga menos viajes que esto, la suma SÍ es
 * su histórico. En cuanto la lista llega al tope, la suma deja de serlo y el
 * rótulo tiene que dejar de prometer un total que no está calculando.
 */
const TOPE_VIAJES_CHOFER = 200;

/**
 * CUÁNTAS TARJETAS SE PINTAN EN EL HISTORIAL, Y CUÁNTO SE LEE DE MÁS.
 *
 * Los dos números tienen que vivir juntos porque dependen el uno del otro.
 * El filtro de «ocultos» es EN MEMORIA y POSTERIOR a la consulta, y tiene que
 * serlo: en Firestore un documento que no trae el campo jamás casa con un
 * `where`, así que filtrar en la consulta le vaciaría la pantalla a todos los
 * usuarios que ya existen hoy (el porqué largo está más abajo, en el bloque
 * OCULTAR DE MI ACTIVIDAD).
 *
 * El problema de leer exactamente lo que se pinta es que entonces cada tarjeta
 * ocultada es un HUECO que no se rellena: quien ocultaba 10 de 30 se quedaba
 * viendo 20, y los 10 viajes que había detrás no subían nunca. Ocultar dejaba
 * de ser «quitar de mi vista» y pasaba a ser «perder registros».
 *
 * Por eso se lee un margen de más y se recorta al pintar: el margen es cuánto
 * puede ocultar el usuario sin que la lista se le acorte.
 */
const TARJETAS_VISIBLES = 30;
const MARGEN_OCULTOS = 30;
const TOPE_LECTURA_HISTORIAL = TARJETAS_VISIBLES + MARGEN_OCULTOS;

/**
 * Recorta la lista que se pinta saltándose los ocultos, y dice cuántos saltó.
 *
 * Va tomando documentos en orden hasta juntar `cupo` tarjetas VISIBLES. Un
 * documento oculto no consume cupo: se cuenta y se sigue. Así ocultar uno
 * destapa el siguiente de verdad, que es lo que la gente espera al ocultarlo.
 *
 * Con el interruptor «Mostrar ocultos» encendido, los saltados SÍ se pintan,
 * pero siguen sin consumir cupo: el interruptor solo AÑADE tarjetas, nunca
 * empuja fuera de la lista a las que ya se veían.
 *
 * El contador que devuelve NO es el largo del array guardado en Firestore ni
 * el total de ocultos que vinieron en la consulta: es cuántos hubo que saltar
 * para llenar ESTA pantalla. Es el único número que cuadra con las tarjetas
 * que aparecen al encender el interruptor — un «Mostrar ocultos (12)» que al
 * encenderlo enseña 3 es otra cifra que miente. Y como el recorrido es el
 * mismo con el interruptor encendido o apagado, la cifra tampoco salta al
 * moverlo.
 */
function recortarConOcultos<T>(
  lista: T[],
  estaOculto: (x: T) => boolean,
  cupo: number,
  mostrarOcultos: boolean
): { visibles: T[]; saltados: number } {
  const salida: T[] = [];
  let contadasVisibles = 0;
  let saltados = 0;
  for (const x of lista) {
    if (contadasVisibles >= cupo) break;
    if (estaOculto(x)) {
      saltados++;
      if (mostrarOcultos) salida.push(x);
      continue;
    }
    salida.push(x);
    contadasVisibles++;
  }
  return { visibles: salida, saltados };
}

/**
 * ¿El fallo es "falta el índice compuesto"? Firestore lo reporta como
 * `failed-precondition` y el texto trae el enlace para crearlo.
 */
function faltaIndice(e: any): boolean {
  const codigo = String(e?.code ?? '').toLowerCase();
  const msg = String(e?.message ?? e ?? '').toLowerCase();
  return (
    codigo.includes('failed-precondition') ||
    msg.includes('failed-precondition') ||
    msg.includes('requires an index') ||
    msg.includes('requiere un índice') ||
    msg.includes('create_composite')
  );
}

/**
 * Escucha una colección filtrada por un campo y ORDENADA por `createdAt` desc.
 *
 * POR QUÉ ORDENADA: `limit(N)` SIN `orderBy` no devuelve los N más recientes.
 * Firestore ordena por id de documento, que es aleatorio, así que en cuanto el
 * usuario pasa de N registros deja de ver los últimos — justo los que busca.
 *
 * POR QUÉ HAY RESPALDO: la consulta ordenada exige un índice compuesto
 * declarado en firestore.indexes.json Y DESPLEGADO
 * (`firebase deploy --only firestore:indexes`). Si el despliegue todavía no
 * ocurrió, Firestore contesta `failed-precondition` y el usuario vería
 * "No pudimos cargar…" en lugar de sus datos. En ese caso reintentamos SIN
 * orden y ordenamos en memoria: se pierde la garantía de "los N más
 * recientes", pero nunca la lista entera.
 */
function escucharOrdenado(
  coleccion: string,
  campo: string,
  valor: string,
  tope: number,
  alRecibir: (docs: any[]) => void,
  alFallar: (mensaje: string) => void
): () => void {
  let cancelada = false;
  let cancelar: (() => void) | null = null;

  // Un solo reintento. Con esta bandera es imposible entrar en bucle si el
  // respaldo también fallara con el mismo código.
  let reintentado = false;

  const manejarError = (conOrden: boolean, e: any) => {
    if (cancelada) return;
    if (conOrden && !reintentado && faltaIndice(e)) {
      reintentado = true;
      console.warn(
        `[activity] ${coleccion}(${campo}+createdAt) sin índice desplegado: reintento sin orden`
      );
      const anterior = cancelar;
      cancelar = null;
      try { anterior?.(); } catch { /* ya estaba cerrada */ }
      const respaldo = suscribir(false);
      if (cancelada) { try { respaldo(); } catch { /* nada */ } }
      else cancelar = respaldo;
      return;
    }
    alFallar(String(e?.message ?? e));
  };

  const suscribir = (conOrden: boolean): (() => void) => {
    if (Platform.OS !== 'web') {
      let ref: any = nativeFirestore().collection(coleccion).where(campo, '==', valor);
      if (conOrden) ref = ref.orderBy('createdAt', 'desc');
      return ref.limit(tope).onSnapshot(
        (snap: any) => alRecibir(snap.docs.map((d: any) => ({ id: d.id, ...d.data() }))),
        (e: any) => manejarError(conOrden, e)
      ) as () => void;
    }
    const partes: any[] = [where(campo, '==', valor)];
    if (conOrden) partes.push(fbOrderBy('createdAt', 'desc'));
    partes.push(fbLimit(tope));
    return onSnapshot(
      query(collection(db, coleccion), ...partes),
      (s) => alRecibir(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => manejarError(conOrden, e)
    ) as () => void;
  };

  const inicial = suscribir(true);
  // Si el fallo llegara SÍNCRONO dentro de `suscribir(true)`, `manejarError` ya
  // dejó el respaldo en `cancelar`: pisarlo aquí lo dejaría huérfano y vivo.
  if (!reintentado && !cancelada) cancelar = inicial;
  else { try { inicial(); } catch { /* nada */ } }

  return () => {
    cancelada = true;
    try { cancelar?.(); } catch { /* ya estaba cerrada */ }
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// OCULTAR DE MI ACTIVIDAD — que NO es borrar, y no puede serlo
// ═══════════════════════════════════════════════════════════════════════════
//
// POR QUÉ NO HAY BOTÓN "BORRAR":
//   1. Las reglas lo impiden: `/rides` y `/food_orders` tienen
//      `allow delete: if false`. Un botón "Borrar" solo daría permission-denied,
//      o sea un error en la cara del usuario cada vez que lo tocara.
//   2. Y no debe existir: nuestra propia política de privacidad (`app/privacy`)
//      ya le promete a la gente que el viaje terminado se conserva como
//      comprobante de LA OTRA PERSONA y por obligación fiscal. Borrarlo sería
//      romper esa promesa; poner un botón que diga "Borrar" y no borrar,
//      mentirle.
// Así que se hace lo que hace Uber: se quita de TU vista, y se dice qué pasa.
//
// POR QUÉ LA LISTA VIVE EN `users/{uid}` Y NO EN EL DOCUMENTO DEL VIAJE:
//   `cancelAttributionOk()` (firestore.rules) rechaza que un pasajero toque
//   CUALQUIER campo de un viaje con `cancelledBy == 'DRIVER'` — justo los que
//   más querría quitarse de encima. Una bandera `oculto` dentro del viaje
//   fallaría exactamente en el caso principal. La regla de `/users`, en cambio,
//   solo tiene lista negra de campos sensibles, y ni `viajesOcultos` ni
//   `pedidosOcultos` están en ella (comprobado en firestore.rules, tanto en el
//   `create` como en el `update`). Sin reglas nuevas y sin índices nuevos.
//
// POR QUÉ EL FILTRO ES EN MEMORIA Y NUNCA UN `where`:
//   en Firestore un documento que NO TIENE el campo jamás casa con un
//   `where('oculto','==',false)`. Filtrar en la consulta le vaciaría la
//   pantalla a todos los usuarios que ya existen hoy.

/** Campo de `users/{uid}` donde vive cada lista de ocultos. */
type CampoOcultos = 'viajesOcultos' | 'pedidosOcultos';

/**
 * Escucha las listas de ocultos del propio usuario.
 *
 * Por snapshot y no por lectura única a propósito: si oculta un viaje desde
 * otro teléfono, esta pantalla se entera sola. Y como el SDK aplica la
 * escritura en local antes de que el servidor conteste, la tarjeta desaparece
 * al instante y REAPARECE sola si las reglas rechazaran el guardado.
 */
function escucharOcultos(
  uid: string,
  alRecibir: (viajes: Set<string>, pedidos: Set<string>) => void
): () => void {
  const aConjunto = (v: any): Set<string> =>
    new Set(Array.isArray(v) ? v.map((x: any) => String(x)).filter(Boolean) : []);
  const entregar = (data: any) =>
    alRecibir(aConjunto(data?.viajesOcultos), aConjunto(data?.pedidosOcultos));
  // Un fallo aquí NO puede vaciar la pantalla ni esconder nada de más: sin
  // listas no se oculta nada, o sea que el usuario ve su actividad completa.
  const alFallar = (e: any) => {
    console.warn('[activity] no se pudo leer tu lista de ocultos:', e?.message ?? e);
    alRecibir(new Set<string>(), new Set<string>());
  };

  if (Platform.OS !== 'web') {
    return nativeFirestore().collection('users').doc(uid).onSnapshot(
      (s: any) => entregar(s?.data?.()),
      alFallar
    ) as () => void;
  }
  return onSnapshot(doc(db, 'users', uid), (s) => entregar(s.data()), alFallar) as () => void;
}

/**
 * Añade o quita un id de la lista de ocultos del usuario.
 *
 * `set(..., { merge: true })` y no `update()`: si el documento del usuario no
 * existiera todavía, `update()` fallaría con not-found y la persona vería un
 * error por algo que no es culpa suya. La regla de `create` de `/users` también
 * admite estos dos campos, así que la mezcla vale en los dos casos.
 *
 * `arrayUnion`/`arrayRemove` y no un array reescrito entero: dos teléfonos
 * ocultando a la vez con el array completo se pisarían el uno al otro.
 */
async function guardarOculto(
  uid: string,
  campo: CampoOcultos,
  id: string,
  ocultar: boolean
): Promise<void> {
  if (Platform.OS !== 'web') {
    const valor: any = ocultar
      ? nativeFirestore.FieldValue.arrayUnion(id)
      : nativeFirestore.FieldValue.arrayRemove(id);
    await nativeFirestore().collection('users').doc(uid).set({ [campo]: valor }, { merge: true });
    return;
  }
  const valor: any = ocultar ? arrayUnion(id) : arrayRemove(id);
  await setDoc(doc(db, 'users', uid), { [campo]: valor }, { merge: true });
}

/**
 * Número a partir de lo que haya. Las ofertas del chofer guardan `price` como
 * STRING (`map.tsx` hace `.toString()` al enviarla), así que un `Number()`
 * pelado sobre un campo que a veces trae símbolos daría NaN y ese NaN
 * contaminaría toda la suma del panel de ganancias.
 * Devuelve null —no 0— cuando no hay número: un cero inventado en la pantalla
 * del dinero es peor que un hueco.
 */
function aNumero(v: any): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const limpio = v.replace(/[^\d.,-]/g, '').replace(',', '.');
    if (!limpio) return null;
    const n = Number(limpio);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * QUIÉN CANCELÓ, dicho con todas las letras.
 *
 * El historial solo pintaba "Cancelado". Con eso, la persona no puede
 * distinguir el viaje que canceló ella del que le canceló el chofer ni del que
 * se venció solo — y el chofer al que le apuntan cancelaciones no tiene forma
 * de ver cuáles fueron suyas antes de que la quinta le bloquee la cuenta.
 *
 * `cancelledBy` es el único campo veraz: las reglas de Firestore impiden
 * físicamente que un chofer escriba 'PASSENGER', que un pasajero escriba
 * 'DRIVER', y que cualquier cliente escriba 'system'. `cancelReason` es texto
 * libre y no se usa para atribuir.
 *
 * Sin `cancelledBy` NO se culpa a nadie: se dice que no quedó registrado. Un
 * "lo canceló el chofer" inventado por descarte es una acusación falsa.
 */
function textoDeCancelacion(
  por: any,
  contexto: 'VIAJE' | 'PEDIDO',
  vencido = false
): string {
  if (vencido) {
    return 'Se venció: ningún chofer lo tomó a tiempo. No cuenta como cancelación tuya.';
  }
  const cosa = contexto === 'VIAJE' ? 'El viaje' : 'El pedido';
  switch (String(por ?? '').trim().toUpperCase()) {
    case 'PASSENGER':
    case 'CUSTOMER':
      return 'Lo cancelaste tú.';
    case 'DRIVER':
      return 'Lo canceló el chofer.';
    case 'RESTAURANT':
      return 'Lo canceló el restaurante.';
    case 'ADMIN':
      return `${cosa} lo canceló nuestro equipo de soporte.`;
    case 'SYSTEM':
      return `${cosa} se venció y el sistema lo cerró. No cuenta como cancelación tuya.`;
    default:
      return `${cosa} fue cancelado. No quedó registrado quién lo canceló.`;
  }
}

export default function ActivityScreen() {
  const role = useAppStore((state) => state.userRole);
  const userUid = useAppStore((s) => s.userFirebaseUid);
  // El radio de trabajo del chofer y cuántas solicitudes hay ahora mismo. El
  // número lo escribe map.tsx, que no se desmonta al cambiar de pestaña, así
  // que aquí no hace falta abrir un segundo oyente ni pagar una lectura más.
  const radioZonaKm = useAppStore((s) => s.radioZonaKm);
  const setRadioZonaKm = useAppStore((s) => s.setRadioZonaKm);
  const solicitudesVisibles = useAppStore((s) => s.solicitudesVisibles);
  const colors = useThemeColors();
  const router = useRouter();

  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // El fallo tiene que VERSE. Antes el error se tragaba con
  // `() => setLoading(false)` y la pantalla decía "Aún no has hecho ningún
  // pedido" a alguien que acababa de pedir.
  const [errorPedidos, setErrorPedidos] = useState<string | null>(null);
  const addToCart = useFoodStore((s) => s.addToCart);
  const clearCart = useFoodStore((s) => s.clearCart);

  // Milisegundos de un `createdAt` de Firestore (Timestamp) o de lo que haya.
  const msDe = (v: any): number => v?.toMillis?.() ?? v?.toDate?.()?.getTime?.() ?? 0;
  const fechaDe = (v: any): string => {
    const ms = msDe(v);
    if (!ms) return 'Sin fecha';
    try {
      return new Date(ms).toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'Sin fecha';
    }
  };

  // FOOD_DELIVERY / PASSENGER: últimos pedidos de comida del cliente.
  useEffect(() => {
    if (!userUid || (role !== 'FOOD_DELIVERY' && role !== 'PASSENGER')) {
      setLoading(false);
      return;
    }
    // Ordenamos también en memoria: si el respaldo sin índice entra en juego,
    // la lista llega sin orden y el usuario vería sus pedidos revueltos.
    const ordenar = (docs: any[]) =>
      [...docs].sort((a, b) => msDe(b.createdAt) - msDe(a.createdAt));

    const unsub = escucharOrdenado(
      // Se leen TARJETAS_VISIBLES + MARGEN_OCULTOS a propósito: ver la
      // constante. Con el tope justo, cada pedido ocultado era un hueco.
      'food_orders', 'customerId', userUid, TOPE_LECTURA_HISTORIAL,
      (docs) => { setOrders(ordenar(docs)); setErrorPedidos(null); setLoading(false); },
      (msg) => {
        console.warn('[activity] no se pudieron leer los pedidos:', msg);
        setErrorPedidos(msg);
        setLoading(false);
      }
    );
    return unsub;
  }, [userUid, role]);

  // VIAJES DEL PASAJERO.
  // Esta consulta NO EXISTÍA: la pantalla solo leía pedidos de comida y aun
  // así el estado vacío afirmaba "Aún no has realizado ningún viaje". Es decir,
  // a un pasajero con viajes hechos la app le decía que no tenía ninguno.
  // El índice rides(passengerId ASC, createdAt DESC) ya está declarado.
  const [viajesPasajero, setViajesPasajero] = useState<any[]>([]);
  const [errorViajesPasajero, setErrorViajesPasajero] = useState<string | null>(null);
  const [viajesPasajeroRecibidos, setViajesPasajeroRecibidos] = useState(false);
  const cargandoViajesPasajero = !!userUid && role === 'PASSENGER' && !viajesPasajeroRecibidos;
  useEffect(() => {
    if (!userUid || role !== 'PASSENGER') return;
    const ordenar = (docs: any[]) =>
      [...docs].sort((a, b) => msDe(b.createdAt) - msDe(a.createdAt));

    const unsub = escucharOrdenado(
      // Mismo motivo que en los pedidos: se lee de más para poder ocultar
      // sin que la lista del pasajero se acorte.
      'rides', 'passengerId', userUid, TOPE_LECTURA_HISTORIAL,
      (docs) => {
        setViajesPasajero(ordenar(docs));
        setErrorViajesPasajero(null);
        setViajesPasajeroRecibidos(true);
      },
      (msg) => {
        console.warn('[activity] no se pudieron leer los viajes del pasajero:', msg);
        setErrorViajesPasajero(msg);
        setViajesPasajeroRecibidos(true);
      }
    );
    return unsub;
  }, [userUid, role]);

  // GANANCIAS REALES DEL CHOFER.
  // El "Panel de Ganancias" mostraba $0.00 escrito a mano, la cuota fija en
  // $1,000 y "Has ahorrado $0.00" — todo inventado. Un chofer que trabajó toda
  // la semana y ve cero en su panel asume que la app le está robando. Ahora se
  // suman sus viajes COMPLETED de verdad.
  const [viajesChofer, setViajesChofer] = useState<any[]>([]);
  const [errorViajes, setErrorViajes] = useState<string | null>(null);
  const [viajesRecibidos, setViajesRecibidos] = useState(false);
  // Derivado: sin uid no hay suscripción que esperar, así que tampoco puede
  // quedarse colgado en "Cargando tus viajes…".
  const cargandoViajes = !!userUid && !viajesRecibidos;
  useEffect(() => {
    if (!userUid || (role !== 'DRIVER' && role !== 'DELIVERY_DRIVER' && role !== 'SCHOOL_DRIVER')) return;
    const ordenar = (docs: any[]) =>
      [...docs].sort((a, b) => msDe(b.createdAt) - msDe(a.createdAt));

    // El índice compuesto (driverId ASC, createdAt DESC) está declarado en
    // firestore.indexes.json.
    const unsub = escucharOrdenado(
      'rides', 'driverId', userUid, TOPE_VIAJES_CHOFER,
      (docs) => { setViajesChofer(ordenar(docs)); setErrorViajes(null); setViajesRecibidos(true); },
      (msg) => {
        console.warn('[activity] no se pudieron leer los viajes del chofer:', msg);
        setErrorViajes(msg);
        setViajesRecibidos(true);
      }
    );
    return unsub;
  }, [userUid, role]);

  // ─────────────────────────────────────────────────────────────────────
  // LO QUE EL USUARIO PIDIÓ QUITAR DE SU VISTA
  // (el porqué de cada decisión está arriba, en `escucharOcultos`)
  // ─────────────────────────────────────────────────────────────────────
  const [viajesOcultos, setViajesOcultos] = useState<Set<string>>(new Set<string>());
  const [pedidosOcultos, setPedidosOcultos] = useState<Set<string>>(new Set<string>());
  const [mostrarOcultos, setMostrarOcultos] = useState(false);
  // Solo los perfiles que PINTAN una lista necesitan esta suscripción. El
  // chofer no: su pestaña es el Panel de Ganancias y no tiene ninguna tarjeta
  // que ocultar, así que abrirle un oyente sobre su propio documento sería
  // gasto puro (y ese documento lo toca el backend a menudo: cada escritura
  // volvería a pintar la pantalla sin que nada haya cambiado para él).
  const pintaListaDeTarjetas = role === 'PASSENGER' || role === 'FOOD_DELIVERY';
  useEffect(() => {
    if (!userUid || !pintaListaDeTarjetas) return;
    const parar = escucharOcultos(userUid, (viajes, pedidos) => {
      setViajesOcultos(viajes);
      setPedidosOcultos(pedidos);
    });
    return () => {
      parar();
      // AL CAMBIAR DE CUENTA HAY QUE SOLTAR LA LISTA ANTERIOR.
      // Si no, quien inicia sesión después en el mismo teléfono arrastra los
      // ids que ocultó el dueño anterior, y aunque no coincidan con nada suyo,
      // dejarlos en memoria es guardar dato de otra persona sin motivo.
      setViajesOcultos(new Set<string>());
      setPedidosOcultos(new Set<string>());
    };
  }, [userUid, pintaListaDeTarjetas]);

  /**
   * El menú de la tarjeta. NUNCA dice "Borrar", porque no borra nada, y el
   * texto explica qué pasa con el registro para que nadie crea que desapareció
   * del mundo ni se lleve la sorpresa después.
   */
  const menuDeTarjeta = (tipo: 'VIAJE' | 'PEDIDO', id: string, estaOculto: boolean) => {
    if (!userUid || !id) return;
    const campo: CampoOcultos = tipo === 'VIAJE' ? 'viajesOcultos' : 'pedidosOcultos';
    const cosa = tipo === 'VIAJE' ? 'El viaje' : 'El pedido';
    const aplicar = (ocultar: boolean) => {
      guardarOculto(userUid, campo, id, ocultar).catch((e: any) => {
        // Si el guardado falla, el snapshot devuelve la tarjeta a su sitio
        // sola. Lo que no se puede hacer es callarlo: el usuario creería que
        // quedó oculto y lo vería otra vez mañana sin entender por qué.
        console.warn('[activity] no se pudo guardar el oculto:', e?.message ?? e);
        Alert.alert(
          ocultar ? 'No pudimos ocultarlo' : 'No pudimos mostrarlo otra vez',
          'No se guardó el cambio. Revisa tu conexión y vuelve a intentarlo.'
        );
      });
    };
    if (estaOculto) {
      Alert.alert(
        'Volver a mostrarlo',
        `${cosa} vuelve a aparecer en tu lista de actividad.`,
        [
          { text: 'Cancelar', style: 'cancel' },
          { text: 'Mostrar', onPress: () => aplicar(false) },
        ]
      );
      return;
    }
    Alert.alert(
      'Ocultar de mi actividad',
      `Se quita de tu lista. ${cosa} sigue guardado como comprobante de la otra persona.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        { text: 'Ocultar de mi actividad', onPress: () => aplicar(true) },
      ]
    );
  };

  /** Los tres puntos de la tarjeta. */
  const BotonMenu = ({ tipo, id, estaOculto }: { tipo: 'VIAJE' | 'PEDIDO'; id: string; estaOculto: boolean }) => (
    <TouchableOpacity
      onPress={() => menuDeTarjeta(tipo, id, estaOculto)}
      style={styles.menuBtn}
      accessibilityRole="button"
      accessibilityLabel={estaOculto ? 'Volver a mostrarlo en mi actividad' : 'Ocultar de mi actividad'}
    >
      <Ionicons name="ellipsis-vertical" size={18} color={colors.textSecondary} />
    </TouchableOpacity>
  );

  // ─────────────────────────────────────────────────────────────────────
  // PRECIO REAL DE CADA VIAJE
  //
  // `finalPrice` NO LO ESCRIBE NADIE en todo el proyecto: leer
  // `finalPrice ?? offerPrice` caía SIEMPRE en la PROPUESTA del pasajero, que
  // no es lo que se cobró cuando el chofer contraofertó. El precio pactado
  // vive en `rides/{id}/offers/{acceptedOfferId}.price` y se guarda como
  // STRING (hay que convertirlo). Es exactamente la misma verdad que usa el
  // panel de administración (functions/src/adminPanel.ts → resolverPrecios).
  //
  // Orden: 1) oferta aceptada  2) `offerPrice` del viaje  3) null ("sin dato").
  // Nunca 0.
  // ─────────────────────────────────────────────────────────────────────
  const [precioOferta, setPrecioOferta] = useState<Record<string, number>>({});
  const [preciosResueltos, setPreciosResueltos] = useState(false);

  const viajesConOferta = useMemo(() => {
    const vistos = new Set<string>();
    const salida: Array<{ id: string; ofertaId: string }> = [];
    for (const r of [...viajesChofer, ...viajesPasajero]) {
      const ofertaId = String(r?.acceptedOfferId ?? '').trim();
      if (!r?.id || !ofertaId || vistos.has(r.id)) continue;
      vistos.add(r.id);
      salida.push({ id: r.id, ofertaId });
    }
    return salida;
  }, [viajesChofer, viajesPasajero]);

  // Clave estable de la lista. Sin ella el efecto se relanzaría con CADA
  // snapshot (aunque el contenido sea idéntico) y volvería a leer las ofertas.
  const claveOfertas = viajesConOferta.map((v) => `${v.id}:${v.ofertaId}`).join('|');

  useEffect(() => {
    // SIN SESIÓN NO HAY NADA QUE RESOLVER, Y HAY QUE DECIRLO.
    // Antes este efecto salía con un `return` pelado y `preciosResueltos` se
    // quedaba en `false` PARA SIEMPRE. Como el panel de abajo se pinta con
    // `cargandoViajes || !preciosResueltos`, el resultado era una pantalla
    // congelada en "Calculando tus ganancias…" que no terminaba nunca — ni
    // recargando, porque no había nada que esperar.
    if (!userUid) {
      setPrecioOferta({});
      setPreciosResueltos(true);
      return;
    }
    if (viajesConOferta.length === 0) {
      setPrecioOferta({});
      setPreciosResueltos(true);
      return;
    }
    let vivo = true;
    setPreciosResueltos(false);
    (async () => {
      const salida: Record<string, number> = {};
      // En lotes: 200 lecturas sueltas a la vez castigan la red del teléfono.
      const LOTE = 20;
      for (let i = 0; i < viajesConOferta.length && vivo; i += LOTE) {
        const trozo = viajesConOferta.slice(i, i + LOTE);
        await Promise.all(
          trozo.map(async ({ id, ofertaId }) => {
            try {
              let data: any = null;
              if (Platform.OS !== 'web') {
                const s: any = await nativeFirestore()
                  .collection('rides').doc(id)
                  .collection('offers').doc(ofertaId)
                  .get();
                // En @react-native-firebase v24 `exists` es MÉTODO, no propiedad.
                data = s?.exists?.() ? s.data() : null;
              } else {
                const s = await getDoc(doc(db, 'rides', id, 'offers', ofertaId));
                data = s.exists() ? s.data() : null;
              }
              // `price` es el campo canónico; los otros dos son respaldos de
              // versiones viejas del cliente que sí existen en documentos
              // antiguos (igual que en adminPanel.ts).
              const p = aNumero(data?.price) ?? aNumero(data?.offerPrice) ?? aNumero(data?.amount);
              if (p !== null && p > 0) salida[id] = p;
            } catch (e: any) {
              console.warn('[activity] no se pudo leer la oferta aceptada de', id, e?.message ?? e);
            }
          })
        );
      }
      if (vivo) {
        setPrecioOferta(salida);
        setPreciosResueltos(true);
      }
    })();
    return () => { vivo = false; };
    // `viajesConOferta` se deriva de los snapshots; `claveOfertas` es su huella
    // estable y es lo que debe disparar el efecto.
  }, [userUid, claveOfertas]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Precio pactado del viaje, o null si no se pudo resolver. NUNCA 0.
   *
   * LA PROPUESTA DEL PASAJERO NO ES UN RESPALDO CUANDO HUBO CONTRAOFERTA.
   * Antes, si la lectura de la oferta aceptada fallaba (conexión, permiso,
   * documento borrado), se caía a `offerPrice`. Pero `offerPrice` es lo que el
   * pasajero OFRECIÓ, y si el viaje tiene `acceptedOfferId` es justamente
   * porque se pactó OTRA cifra: el chofer contraofertó. O sea, el respaldo era
   * un número que se sabe equivocado, presentado como si fuera lo que cobró.
   *
   * Regla: si el viaje tiene oferta aceptada y no pudimos leerla → "sin dato".
   * Si NO tiene oferta aceptada, nadie contraofertó y `offerPrice` sí es el
   * precio del viaje.
   */
  const precioDe = (r: any): number | null => {
    const pactado = precioOferta[String(r?.id ?? '')];
    if (typeof pactado === 'number' && pactado > 0) return pactado;
    if (String(r?.acceptedOfferId ?? '').trim()) return null;
    const propuesta = aNumero(r?.offerPrice);
    return propuesta !== null && propuesta > 0 ? propuesta : null;
  };

  /**
   * ¿Todavía estamos yendo a buscar el precio pactado de ESTE viaje?
   * Sirve para no enseñar "sin dato" por un instante y cambiarlo a una cifra
   * medio segundo después: mientras se resuelve decimos que se está calculando.
   */
  const precioEnCamino = (r: any): boolean =>
    !preciosResueltos &&
    !!String(r?.acceptedOfferId ?? '').trim() &&
    precioOferta[String(r?.id ?? '')] === undefined;

  const sumar = (lista: any[]): { total: number; sinDato: number } =>
    lista.reduce<{ total: number; sinDato: number }>(
      (acc, r) => {
        const p = precioDe(r);
        if (p === null) return { total: acc.total, sinDato: acc.sinDato + 1 };
        return { total: acc.total + p, sinDato: acc.sinDato };
      },
      { total: 0, sinDato: 0 }
    );

  const completados = viajesChofer.filter((r: any) => r.status === 'COMPLETED');
  const inicioMes = new Date();
  inicioMes.setDate(1);
  inicioMes.setHours(0, 0, 0, 0);
  const completadosMes = completados.filter((r: any) => msDe(r.createdAt) >= inicioMes.getTime());

  const historico = sumar(completados);

  /**
   * Las tres horas en que a ESTE chofer le entran más viajes.
   *
   * Se calcula sobre `completados`, que ya está en memoria: cero lecturas
   * nuevas. Es lo honesto que se puede entregar hoy en lugar de un mapa de
   * demanda — un mapa necesita volumen, y con pocos viajes no señala una zona
   * sino la casa de una persona.
   *
   * Pide un mínimo de 3 viajes: con uno o dos, «tu mejor hora» es ruido con
   * pinta de dato.
   */
  const mejoresHoras = useMemo(() => {
    if (completados.length < 3) return [] as { hora: number; n: number; rotulo: string }[];
    const porHora = new Map<number, number>();
    for (const r of completados) {
      const ms = msDe((r as any).createdAt);
      if (!ms) continue;
      const h = new Date(ms).getHours();
      porHora.set(h, (porHora.get(h) ?? 0) + 1);
    }
    const dosDigitos = (n: number) => String(n).padStart(2, '0');
    return [...porHora.entries()]
      .sort((a, b) => b[1] - a[1] || a[0] - b[0])
      .slice(0, 3)
      .map(([hora, n]) => ({ hora, n, rotulo: `${dosDigitos(hora)}:00 – ${dosDigitos((hora + 1) % 24)}:00` }));
  }, [completados]);
  const delMes = sumar(completadosMes);
  // Lo que le habría costado ese mismo trabajo en una app que cobra 30%.
  const ahorroComision = Math.round(historico.total * 0.3);
  /** Un precio que no se pudo resolver se dice, no se rellena con un cero. */
  const moneyODato = (n: number | null) => (n === null ? 'sin dato' : pesos(n));

  /**
   * Volver a pedir — clona los items del pedido al carrito.
   * Parsea el string "2x Hamburguesa, 1x Coca-Cola" y los recompone como
   * objetos de carrito. Si el restaurante ya no existe / no está APPROVED,
   * avisamos al usuario.
   */
  const handleReorder = (order: any) => {
    const restaurantId = order.restaurantId;
    if (!restaurantId) {
      Alert.alert('Sin info', 'Este pedido no tiene restaurante asociado.');
      return;
    }
    Alert.alert(
      'Volver a pedir',
      `¿Quieres repetir tu pedido de ${order.restaurantName ?? 'este restaurante'}?\n\nSe reemplazará lo que tengas ahora en el carrito.`,
      [
        { text: 'Cancelar', style: 'cancel' },
        {
          text: 'Sí, repetir',
          onPress: () => {
            clearCart();
            // Parsear items: "2x Hamburguesa Doble, 1x Coca Cola"
            const items = typeof order.items === 'string' ? order.items.split(',') : [];
            const lineRegex = /^\s*(\d+)\s*x\s*(.+?)\s*$/i;
            for (const line of items) {
              const m = line.match(lineRegex);
              if (!m) continue;
              const qty = parseInt(m[1], 10);
              const name = m[2].trim();
              addToCart(
                {
                  id: `reorder-${name}`,
                  name,
                  description: '',
                  price: Math.round((order.total ?? 0) / Math.max(1, items.length)),
                  category: 'reorder',
                  imageUrl: '',
                  isAvailable: true,
                  prepTime: '',
                },
                restaurantId,
                qty
              );
            }
            router.push('/cart');
          },
        },
      ]
    );
  };

  const STATUS_LABEL: Record<string, { label: string; color: string; icon: keyof typeof Ionicons.glyphMap }> = {
    PENDING: { label: 'Pendiente', color: '#FF9500', icon: 'time' },
    PREPARING: { label: 'Preparando', color: '#FF9500', icon: 'restaurant' },
    READY: { label: 'Listo', color: '#0066CC', icon: 'fast-food' },
    ASSIGNED: { label: 'Recogiendo', color: '#0066CC', icon: 'bicycle' },
    PICKED_UP: { label: 'En camino', color: '#00A86B', icon: 'navigate' },
    DELIVERED: { label: 'Entregado', color: '#2E7D32', icon: 'checkmark-circle' },
    CANCELLED: { label: 'Cancelado', color: '#D32F2F', icon: 'close-circle' },
  };

  const RIDE_STATUS_LABEL: Record<string, { label: string; color: string; icon: keyof typeof Ionicons.glyphMap }> = {
    PENDING: { label: 'Buscando chofer', color: '#FF9500', icon: 'time' },
    ACCEPTED: { label: 'Chofer asignado', color: '#0066CC', icon: 'car' },
    ARRIVED: { label: 'Chofer en el punto', color: '#0066CC', icon: 'location' },
    IN_PROGRESS: { label: 'En viaje', color: '#00A86B', icon: 'navigate' },
    COMPLETED: { label: 'Completado', color: '#2E7D32', icon: 'checkmark-circle' },
    CANCELLED: { label: 'Cancelado', color: '#D32F2F', icon: 'close-circle' },
    EXPIRED: { label: 'Expirado', color: '#78909C', icon: 'alert-circle' },
  };

  /** Aviso de "no pudimos leer ESTO" — nombra siempre qué falta. */
  const AvisoDeFallo = ({ que, explicacion }: { que: string; explicacion: string }) => (
    <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: '#D32F2F', marginBottom: 12 }]}>
      <Ionicons name="cloud-offline-outline" size={48} color="#D32F2F" style={{ marginBottom: 12 }} />
      <Text style={[styles.emptyText, { color: '#D32F2F', fontWeight: '700', marginBottom: 6 }]}>
        No pudimos cargar {que}
      </Text>
      <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{explicacion}</Text>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────
  // EL FILTRO DE OCULTOS — EN MEMORIA, NUNCA EN LA CONSULTA.
  //
  // Antes esto era un `filter` pelado sobre lo que trajo la consulta, y por eso
  // ocultar una tarjeta NO destapaba la siguiente: se leían 30 y se pintaban
  // las 30 menos las ocultas, así que quien ocultaba 10 se quedaba viendo 20 y
  // los que había detrás no subían nunca. Ahora la consulta trae un margen de
  // más y `recortarConOcultos` (arriba, con el porqué completo) va tomando
  // hasta llenar TARJETAS_VISIBLES saltándose las ocultas.
  //
  // El contador cuenta lo que ESTA pantalla está escondiendo ahora mismo, y no
  // el largo del array guardado: ese puede traer ids de cosas más viejas que
  // las que se leen aquí, y un "Mostrar ocultos (12)" que al encenderlo enseña
  // 3 tarjetas es otra cifra que miente.
  // ─────────────────────────────────────────────────────────────────────
  const recorteViajes = recortarConOcultos(
    viajesPasajero,
    (v: any) => viajesOcultos.has(String(v?.id)),
    TARJETAS_VISIBLES,
    mostrarOcultos
  );
  const recortePedidos = recortarConOcultos(
    orders,
    (o: any) => pedidosOcultos.has(String(o?.id)),
    TARJETAS_VISIBLES,
    mostrarOcultos
  );
  const totalOcultosEnPantalla = recorteViajes.saltados + recortePedidos.saltados;
  const viajesVisibles = recorteViajes.visibles;
  const pedidosVisibles = recortePedidos.visibles;

  // ─────────────────────────────────────────────────────────────
  // FOOD_DELIVERY / PASSENGER → historial
  // ─────────────────────────────────────────────────────────────
  if (role === 'PASSENGER' || role === 'FOOD_DELIVERY') {
    const esPasajero = role === 'PASSENGER';
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
        <ScrollView contentContainerStyle={styles.container}>
          <Text style={[styles.title, { color: colors.text }]}>
            {esPasajero ? 'Tu Actividad' : 'Tus Pedidos'}
          </Text>

          {/* ══════════════════════════════════════════════════════════════
              POR QUÉ SOLO SE PUEDE OCULTAR, Y NO BORRAR
              ══════════════════════════════════════════════════════════════
              El fundador: «el usuario no tiene cómo borrar los viajes que
              realizó, solo un botón que dice ocultar mi actividad».

              Y no se puede borrar a propósito: `firestore.rules` tiene
              `allow delete: if false` en /rides y en /food_orders. Un botón
              «Borrar» solo daría «permiso denegado». El motivo es que un viaje
              es de DOS personas: borrarlo le arrancaría a la otra su
              comprobante de lo que pasó y de lo que se acordó — y de ahí
              cuelgan también la contabilidad del mes y cualquier reporte que
              alguien haya hecho sobre ese viaje.

              Lo que SÍ estaba mal es que eso solo se explicaba DENTRO del
              diálogo de confirmación, detrás de un icono de tres puntos sin
              rótulo. Mirando la pantalla, lo único que se veía era «ocultar» —
              o sea, la sensación exacta de que la app le esconde sus datos.
              Ahora se dice de frente, y se dice dónde sí puede borrarlo todo. */}
          <Text style={[styles.notaRetencion, { color: colors.textSecondary }]}>
            Puedes <Text style={{ fontWeight: '700' }}>ocultar</Text> lo que no quieras ver aquí. No se
            borra porque {esPasajero ? 'un viaje' : 'un pedido'} es de dos personas: es el comprobante de
            la otra parte de lo que se acordó. Si quieres que no quede nada tuyo, puedes borrar tu cuenta
            entera desde <Text style={{ fontWeight: '700' }}>Perfil → Eliminar mi cuenta</Text>.
          </Text>

          {/* EL INTERRUPTOR DE RECUPERACIÓN. Solo aparece cuando hay algo
              oculto que recuperar: un interruptor permanente en cero no le
              sirve a nadie y ensucia la pantalla de quien nunca ocultó nada. */}
          {totalOcultosEnPantalla > 0 && (
            <View style={[styles.filaOcultos, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons name="eye-off-outline" size={18} color={colors.textSecondary} />
              <Text style={{ flex: 1, marginLeft: 10, fontSize: 13, color: colors.textSecondary }}>
                Mostrar ocultos ({totalOcultosEnPantalla})
              </Text>
              <Switch
                value={mostrarOcultos}
                onValueChange={setMostrarOcultos}
                trackColor={{ false: colors.border, true: colors.primary }}
                accessibilityLabel={`Mostrar lo que ocultaste: ${totalOcultosEnPantalla}`}
              />
            </View>
          )}

          {/* VIAJES — solo el pasajero. Va en su propio bloque a propósito:
              si falla esta consulta, los pedidos de abajo se siguen viendo, y
              al revés. Antes un solo fallo borraba de pantalla lo que sí había
              llegado de la otra consulta. */}
          {esPasajero && (
            <>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Tus viajes</Text>
              {cargandoViajesPasajero ? (
                <Text style={{ color: colors.textSecondary, marginBottom: 16 }}>
                  Cargando tus viajes…
                </Text>
              ) : errorViajesPasajero ? (
                <AvisoDeFallo
                  que="tus viajes"
                  explicacion="Esto no quiere decir que no tengas ninguno: tus viajes siguen guardados. Revisa tu conexión y vuelve a entrar a esta pantalla. Tus pedidos de comida se muestran abajo igualmente."
                />
              ) : viajesVisibles.length === 0 ? (
                <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border, marginBottom: 16 }]}>
                  <Ionicons name="car-outline" size={48} color={colors.textSecondary} style={{ marginBottom: 12 }} />
                  {/* "No tienes viajes" y "los ocultaste todos" NO son lo mismo.
                      Decir lo primero cuando pasa lo segundo es exactamente el
                      error que ya se corrigió una vez en esta pantalla. */}
                  <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                    {viajesPasajero.length > 0
                      ? 'Ocultaste todos los viajes de esta lista. Enciende «Mostrar ocultos» arriba para verlos otra vez.'
                      : 'Aún no has realizado ningún viaje con Go Safely.'}
                  </Text>
                </View>
              ) : (
                viajesVisibles.map((v) => {
                  const st = RIDE_STATUS_LABEL[v.status] ?? {
                    label: v.status ?? 'Sin estado',
                    color: colors.textSecondary,
                    icon: 'help' as const,
                  };
                  const precio = precioDe(v);
                  const estaOculto = viajesOcultos.has(String(v?.id));
                  return (
                    <View
                      key={v.id}
                      style={[
                        styles.orderCard,
                        { backgroundColor: colors.card, borderColor: colors.border },
                        // Se ve, pero APAGADA: así se distingue de un golpe de
                        // vista lo que está fuera de la lista de todos los días.
                        estaOculto && { opacity: 0.55 },
                      ]}
                    >
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <View style={{ flex: 1 }}>
                          <Text style={[styles.orderRest, { color: colors.text }]} numberOfLines={1}>
                            {v.destination ?? 'Destino no registrado'}
                          </Text>
                          <Text style={[styles.orderItems, { color: colors.textSecondary }]} numberOfLines={2}>
                            Desde {v.originAddress ?? 'origen no registrado'} · {fechaDe(v.createdAt)}
                          </Text>
                        </View>
                        {/* "sin dato" y no RD$0: un cero inventado en la
                            pantalla del dinero es peor que un hueco.
                            Y mientras vamos a buscar el precio pactado se dice
                            que se está calculando, para no parpadear de
                            "sin dato" a una cifra. */}
                        <Text
                          style={[
                            styles.orderTotal,
                            { color: precio === null ? colors.textSecondary : colors.primary },
                            precio === null && { fontSize: 13, fontWeight: '600' },
                            precioEnCamino(v) && { fontSize: 13, fontWeight: '600', color: colors.textSecondary },
                          ]}
                        >
                          {precioEnCamino(v) ? 'calculando…' : moneyODato(precio)}
                        </Text>
                        <BotonMenu tipo="VIAJE" id={String(v?.id ?? '')} estaOculto={estaOculto} />
                      </View>

                      <View style={styles.orderBottomRow}>
                        <View style={[styles.statusPill, { backgroundColor: st.color + '20' }]}>
                          <Ionicons name={st.icon} size={11} color={st.color} />
                          <Text style={[styles.statusText, { color: st.color }]}>{st.label}</Text>
                        </View>
                        {/* Solo se ve con el interruptor encendido, y ahí hace
                            falta: sin él no se sabría cuál de las tarjetas es
                            la que se había quitado. */}
                        {estaOculto && (
                          <View style={[styles.statusPill, { backgroundColor: colors.textSecondary + '20' }]}>
                            <Ionicons name="eye-off" size={11} color={colors.textSecondary} />
                            <Text style={[styles.statusText, { color: colors.textSecondary }]}>Oculto</Text>
                          </View>
                        )}
                        {v.driverName ? (
                          <Text style={{ fontSize: 12, color: colors.textSecondary }} numberOfLines={1}>
                            Chofer: {v.driverName}
                          </Text>
                        ) : null}
                      </View>

                      {(v.status === 'CANCELLED' || v.status === 'EXPIRED') && (
                        <Text style={[styles.motivoCancelacion, { color: colors.textSecondary }]}>
                          {textoDeCancelacion(v.cancelledBy, 'VIAJE', v.status === 'EXPIRED')}
                        </Text>
                      )}
                    </View>
                  );
                })
              )}

              <Text style={[styles.sectionTitle, { color: colors.text, marginTop: 10 }]}>
                Tus pedidos de comida
              </Text>
            </>
          )}

          {loading ? (
            <Text style={{ color: colors.textSecondary, textAlign: 'center', marginTop: 20 }}>
              Cargando…
            </Text>
          ) : errorPedidos ? (
            // "No pudimos leer" ≠ "no tienes pedidos". Confundirlos es lo que
            // hace pensar al cliente que su pedido (y su dinero) se perdió.
            <AvisoDeFallo
              que="tus pedidos"
              explicacion="Esto no quiere decir que no tengas ninguno. Revisa tu conexión y vuelve a entrar a esta pantalla."
            />
          ) : pedidosVisibles.length === 0 ? (
            <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Ionicons
                name="fast-food-outline"
                size={60}
                color={colors.textSecondary}
                style={{ marginBottom: 16 }}
              />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
                {orders.length > 0
                  ? 'Ocultaste todos los pedidos de esta lista. Enciende «Mostrar ocultos» arriba para verlos otra vez.'
                  : 'Aún no has hecho ningún pedido.'}
              </Text>
            </View>
          ) : (
            pedidosVisibles.map((o) => {
              const status = STATUS_LABEL[o.status] ?? { label: o.status, color: colors.textSecondary, icon: 'help' as const };
              const canReorder = o.status === 'DELIVERED' || o.status === 'CANCELLED';
              const estaOculto = pedidosOcultos.has(String(o?.id));
              return (
                <View
                  key={o.id}
                  style={[
                    styles.orderCard,
                    { backgroundColor: colors.card, borderColor: colors.border },
                    estaOculto && { opacity: 0.55 },
                  ]}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <View style={{ flex: 1 }}>
                      <Text style={[styles.orderRest, { color: colors.text }]} numberOfLines={1}>
                        {o.restaurantName ?? 'Restaurante'}
                      </Text>
                      <Text style={[styles.orderItems, { color: colors.textSecondary }]} numberOfLines={2}>
                        {o.items ?? '—'}
                      </Text>
                    </View>
                    <Text style={[styles.orderTotal, { color: colors.primary }]}>{pesos(o.total ?? 0)}</Text>
                    <BotonMenu tipo="PEDIDO" id={String(o?.id ?? '')} estaOculto={estaOculto} />
                  </View>

                  <View style={styles.orderBottomRow}>
                    <View style={[styles.statusPill, { backgroundColor: status.color + '20' }]}>
                      <Ionicons name={status.icon} size={11} color={status.color} />
                      <Text style={[styles.statusText, { color: status.color }]}>{status.label}</Text>
                    </View>
                    {estaOculto && (
                      <View style={[styles.statusPill, { backgroundColor: colors.textSecondary + '20' }]}>
                        <Ionicons name="eye-off" size={11} color={colors.textSecondary} />
                        <Text style={[styles.statusText, { color: colors.textSecondary }]}>Oculto</Text>
                      </View>
                    )}

                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      <TouchableOpacity
                        style={[styles.trackBtn, { borderColor: colors.textSecondary }]}
                        onPress={() => router.push(`/order-receipt?orderId=${o.id}` as any)}
                      >
                        <Ionicons name="receipt-outline" size={12} color={colors.textSecondary} />
                        <Text style={[styles.trackBtnText, { color: colors.textSecondary }]}>Recibo</Text>
                      </TouchableOpacity>
                      {['ASSIGNED', 'PICKED_UP', 'READY'].includes(o.status) && (
                        <TouchableOpacity
                          style={[styles.trackBtn, { borderColor: colors.primary }]}
                          onPress={() => router.push(`/order-tracking?orderId=${o.id}` as any)}
                        >
                          <Ionicons name="navigate" size={12} color={colors.primary} />
                          <Text style={[styles.trackBtnText, { color: colors.primary }]}>Seguir</Text>
                        </TouchableOpacity>
                      )}
                      {o.status === 'DELIVERED' && o.rated !== true && (
                        <TouchableOpacity
                          style={[styles.trackBtn, { borderColor: '#FF9500' }]}
                          onPress={() => router.push(`/rate-order?orderId=${o.id}` as any)}
                        >
                          <Ionicons name="star" size={12} color="#FF9500" />
                          <Text style={[styles.trackBtnText, { color: '#FF9500' }]}>Calificar</Text>
                        </TouchableOpacity>
                      )}
                      {canReorder && (
                        <TouchableOpacity
                          style={[styles.reorderBtn]}
                          onPress={() => handleReorder(o)}
                        >
                          <Ionicons name="refresh" size={12} color="#FFF" />
                          <Text style={styles.reorderBtnText}>Volver a pedir</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>

                  {o.status === 'CANCELLED' && (
                    <Text style={[styles.motivoCancelacion, { color: colors.textSecondary }]}>
                      {textoDeCancelacion(o.cancelledBy, 'PEDIDO')}
                    </Text>
                  )}
                </View>
              );
            })
          )}
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // FALLBACK — cualquier rol que no tenga vista propia aquí.
  //
  // Antes el Panel de Ganancias del chofer era el `return` final, o sea el
  // CAJÓN DE SASTRE: todo rol que no fuera pasajero caía en él. El invitado
  // veía "Este mes RD$0 · 0 viajes" y bonos por referir choferes, aunque
  // hubiera entrado a explorar como dueño de restaurante. Y le pasaría igual
  // a cualquier rol nuevo que se agregue en el futuro.
  //
  // Ahora el panel del chofer es un caso EXPLÍCITO (abajo) y esto es la
  // salida honesta para los demás.
  const esChofer = role === 'DRIVER' || role === 'DELIVERY_DRIVER' || role === 'SCHOOL_DRIVER';
  if (!esChofer) {
    return (
      <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
        <ScrollView contentContainerStyle={styles.container}>
          <Text style={[styles.title, { color: colors.text }]}>Tu Actividad</Text>
          <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Ionicons name="time-outline" size={60} color={colors.textSecondary} style={{ marginBottom: 16 }} />
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
              {esInvitado()
                ? 'Aquí verás tu historial cuando tengas una cuenta y empieces a usar Go Safely.'
                : 'Todavía no hay actividad que mostrar en este perfil.'}
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // DRIVER EARNINGS DASHBOARD (rol Conductor) — caso explícito
  // ─────────────────────────────────────────────────────────────

  // ESTE PANEL SOLO SUMA `rides` (viajes de pasajeros).
  // Las entregas del repartidor viven en `food_orders` y las rutas del chofer
  // escolar en el módulo escolar; esta pantalla no lee ninguna de las dos. A
  // ellos, por tanto, "Ganancias totales RD$ 0" no es un dato: es una
  // afirmación falsa sobre su trabajo. Se les dice qué se está contando y qué
  // no, y cuando no hay ni un viaje de pasajero no se pinta un cero.
  const soloCuentaViajesDePasajeros = role === 'DELIVERY_DRIVER' || role === 'SCHOOL_DRIVER';

  // ¿LA CONSULTA SE QUEDÓ CORTA?
  // Si devolvió exactamente el tope, es que hay más viajes de los que se
  // leyeron: la suma de abajo NO es el histórico del chofer, y por tanto el
  // rótulo no puede llamarla "Ganancias totales".
  const listaAlTope = viajesChofer.length >= TOPE_VIAJES_CHOFER;
  const trabajoNoContado =
    role === 'DELIVERY_DRIVER' ? 'tus entregas de comida'
    : role === 'SCHOOL_DRIVER' ? 'tus rutas escolares'
    : null;

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={[styles.title, { color: colors.text }]}>Panel de Ganancias</Text>

        {/* Mostrarle RD$0 a un chofer que trabajó toda la semana porque la
            consulta falló es la forma más rápida de que crea que la app le
            está robando. Si no pudimos leer sus viajes NO enseñamos ninguna
            cifra: una cifra falsa es peor que ninguna. */}
        {!userUid ? (
          // RAMA EXPLÍCITA DEL INVITADO / SIN SESIÓN.
          // Sin uid no hay ninguna consulta viva, así que no hay nada que
          // esperar ni nada que sumar. Antes esto no existía y la pantalla se
          // quedaba clavada en "Calculando tus ganancias…" indefinidamente.
          <View style={[styles.emptyState, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Ionicons name="wallet-outline" size={48} color={colors.textSecondary} style={{ marginBottom: 12 }} />
            <Text style={[styles.emptyText, { color: colors.text, fontWeight: '700', marginBottom: 6 }]}>
              Aquí verás lo que ganas
            </Text>
            <Text style={[styles.emptyText, { color: colors.textSecondary }]}>
              {esInvitado()
                ? 'Estás explorando sin cuenta, así que todavía no hay viajes tuyos que sumar. Con tu cuenta aparecerán aquí tus ganancias reales.'
                : 'Inicia sesión con tu cuenta de chofer para ver tus ganancias.'}
            </Text>
            <TouchableOpacity
              onPress={() => router.push('/auth')}
              style={{ marginTop: 18, backgroundColor: colors.primary, paddingHorizontal: 22, paddingVertical: 12, borderRadius: 12 }}
            >
              <Text style={{ color: '#FFF', fontWeight: '700' }}>
                {esInvitado() ? 'Crear mi cuenta' : 'Iniciar sesión'}
              </Text>
            </TouchableOpacity>
          </View>
        ) : errorViajes ? (
          <AvisoDeFallo
            que="tus viajes"
            explicacion="Por eso no te mostramos cifras: serían falsas. Tus ganancias están guardadas. Revisa tu conexión y vuelve a entrar a esta pantalla."
          />
        ) : cargandoViajes || !preciosResueltos ? (
          // Tampoco pintamos una cifra a medias: mientras se resuelve el
          // precio pactado de cada viaje, el total todavía no es el total.
          <Text style={{ color: colors.textSecondary }}>Calculando tus ganancias…</Text>
        ) : (
        <>
        <View style={styles.statsContainer}>
          <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              {soloCuentaViajesDePasajeros ? 'Este mes en viajes' : 'Este mes'}
            </Text>
            {/* Cuando el panel no puede ver TODO el trabajo de esta persona y
                además no hay ni un viaje que sumar, un "RD$ 0" se lee como
                "no ganaste nada". Se dice que no hay dato, no se pinta un cero. */}
            <Text
              style={[
                styles.statValue,
                { color: colors.accent },
                soloCuentaViajesDePasajeros && completadosMes.length === 0 && { fontSize: 16, color: colors.textSecondary },
              ]}
            >
              {soloCuentaViajesDePasajeros && completadosMes.length === 0 ? 'sin dato' : pesos(delMes.total)}
            </Text>
            <Text style={[styles.statSub, { color: colors.textSecondary }]}>
              {completadosMes.length === 0
                ? 'Aún sin viajes este mes'
                : `${completadosMes.length} viaje${completadosMes.length === 1 ? '' : 's'} completado${completadosMes.length === 1 ? '' : 's'}`}
            </Text>
          </View>
          <View style={[styles.statCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {/* EL RÓTULO NO PUEDE PROMETER MÁS DE LO QUE LA CIFRA SUMA.
                La consulta trae como mucho TOPE_VIAJES_CHOFER viajes. Mientras
                el chofer tenga menos, "Ganancias totales" es verdad. Pasado el
                tope deja de serlo: un chofer veterano vería menos dinero del
                que ganó bajo un rótulo que dice "totales", y de ahí a "esta app
                me está robando" hay un solo paso. Se dice qué se está sumando. */}
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              {listaAlTope
                ? `Últimos ${TOPE_VIAJES_CHOFER} viajes`
                : soloCuentaViajesDePasajeros ? 'Total en viajes' : 'Ganancias totales'}
            </Text>
            <Text
              style={[
                styles.statValue,
                { color: colors.primary },
                soloCuentaViajesDePasajeros && completados.length === 0 && { fontSize: 16, color: colors.textSecondary },
              ]}
            >
              {soloCuentaViajesDePasajeros && completados.length === 0 ? 'sin dato' : pesos(historico.total)}
            </Text>
            {/* La misma mentira, en la misma tarjeta: "N viajes en total"
                cuando solo se leyeron los últimos TOPE_VIAJES_CHOFER. */}
            <Text style={[styles.statSub, { color: colors.textSecondary }]}>
              {completados.length} viaje{completados.length === 1 ? '' : 's'} completado{completados.length === 1 ? '' : 's'}
              {listaAlTope ? ` de los últimos ${TOPE_VIAJES_CHOFER}` : ' en total'}
            </Text>
          </View>
        </View>

        {/* ══════════════════════════════════════════════════════════════
            MI ZONA DE TRABAJO
            ══════════════════════════════════════════════════════════════
            Pedido del fundador: «hay choferes que les gusta centrarse dentro
            de una misma zona». Aquí, en Actividad, porque es donde él quiso
            que estuviera y porque es la pantalla que el chofer abre cuando
            piensa en su trabajo, no cuando está manejando.

            LO QUE HACE Y LO QUE NO, y por eso las dos frases de abajo no se
            pueden quitar: NO esconde ni un viaje. Solo decide por cuáles
            suena el teléfono y cuáles se rotulan «Lejos de ti». Esconderlos
            sería peligroso: el reparto del servidor avisa a UN solo chofer y
            no reintenta nunca, así que el viaje que nadie ve se muere a los
            10 minutos con «No encontramos chofer».

            Solo para el chofer de viajes: esta pantalla la comparten el
            repartidor y el chofer escolar, y su feed no pasa por este radio.

            ponytail: límites conocidos de este atajo, para saber cuándo hay
            que subirlo de categoría —
              (a) el día que un incentivo mida PRESENCIA en vez de viajes
                  completados, este radio se vuelve la forma de cobrarlo sin
                  trabajar, y entonces habrá que mirarlo desde el servidor;
              (b) el día que haya varios choferes por zona, tendrá sentido que
                  el reparto lo respete de verdad — y ese día hay que decidir
                  primero qué pasa con el viaje que queda fuera de la zona del
                  único chofer libre;
              (c) la zona es un radio alrededor de DONDE ESTÁ, no un punto
                  fijado en el mapa. Si alguien pide «quiero quedarme en Los
                  Mina aunque esté en mi casa», eso es un selector y un mapa. */}
        {role === 'DRIVER' && (
          <View style={[styles.zonaCaja, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <View style={styles.zonaCabecera}>
              <Ionicons name="locate-outline" size={18} color={colors.primary} />
              <Text style={[styles.zonaTitulo, { color: colors.text }]}>Mi zona de trabajo</Text>
            </View>

            <Text style={[styles.zonaAyuda, { color: colors.textSecondary }]}>
              El teléfono te suena solo por las recogidas que estén a menos de{' '}
              <Text style={{ fontWeight: '800', color: colors.text }}>{radioZonaKm} km</Text> de ti.
            </Text>

            <View style={styles.zonaChips}>
              {[3, 6, 15].map((km) => {
                const activo = radioZonaKm === km;
                return (
                  <TouchableOpacity
                    key={km}
                    onPress={() => setRadioZonaKm(km)}
                    style={[
                      styles.zonaChip,
                      {
                        backgroundColor: activo ? colors.primary : 'transparent',
                        borderColor: activo ? colors.primary : colors.border,
                      },
                    ]}
                  >
                    <Text style={[styles.zonaChipTexto, { color: activo ? '#fff' : colors.text }]}>
                      {km} km
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {/* LAS DOS FRASES QUE NO SE PUEDEN OMITIR. Sin la primera, el
                chofer cree que está rechazando trabajo y no lo usa; sin la
                segunda, cree que la app le esconde viajes. */}
            <Text style={[styles.zonaNota, { color: colors.textSecondary }]}>
              <Text style={{ fontWeight: '800' }}>No esconde viajes.</Text> Los de fuera te siguen
              apareciendo, marcados en ámbar con la distancia, por si te conviene uno.
            </Text>
            <Text style={[styles.zonaNota, { color: colors.textSecondary }]}>
              Y si el sistema te asigna un viaje a ti, suena siempre, esté donde esté.
            </Text>

            <Text style={[styles.zonaAhora, { color: colors.textSecondary }]}>
              {solicitudesVisibles > 0
                ? `Ahora mismo hay ${solicitudesVisibles} solicitud${solicitudesVisibles === 1 ? '' : 'es'} en pantalla.`
                : 'Ahora mismo no hay solicitudes en pantalla.'}
            </Text>

            {/* ── VER DÓNDE ESTÁN, EN EL MAPA ─────────────────────────────
                Lo pidió el fundador: «la opción que lleva a ver en el mapa
                dónde hay más solicitudes de viaje».

                Lo que enseña son las solicitudes VIVAS de ahora mismo, no un
                mapa de calor del histórico. Es la única versión honesta que
                se puede dar hoy —con los viajes que hay, un mapa histórico no
                señalaría una zona sino la casa de alguien— y además es la más
                útil: al chofer le sirve para moverse AHORA, no para saber
                dónde hubo gente la semana pasada.

                Y no cuesta ni una lectura: son las mismas solicitudes que su
                teléfono ya tiene cargadas y que ya puede aceptar una por una.
                No se le enseña nada que no viera ya. */}
            <TouchableOpacity
              onPress={() => router.push('/(tabs)/map' as any)}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Ver en el mapa dónde están las solicitudes"
              style={[styles.zonaBoton, { borderColor: colors.primary }]}
            >
              <Ionicons name="map-outline" size={17} color={colors.primary} />
              <Text style={[styles.zonaBotonTexto, { color: colors.primary }]}>
                {solicitudesVisibles > 0
                  ? `Ver en el mapa dónde están (${solicitudesVisibles})`
                  : 'Ver el mapa de solicitudes'}
              </Text>
            </TouchableOpacity>
            {solicitudesVisibles === 0 && (
              <Text style={[styles.zonaNota, { color: colors.textSecondary }]}>
                Tienes que estar <Text style={{ fontWeight: '800' }}>en línea</Text> para que te
                entren solicitudes y verlas en el mapa.
              </Text>
            )}

            {/* ── TUS MEJORES HORAS ──────────────────────────────────────
                Lo honesto que se puede entregar hoy en lugar de un mapa de
                demanda: con los viajes completados que YA están en memoria,
                sin una lectura más. Un mapa de verdad necesita volumen —por
                debajo de cinco viajes en el mismo kilómetro no señala una
                zona, señala la casa de alguien— así que mientras tanto se le
                dice al chofer lo único que sus propios datos sí sostienen:
                a qué hora le entran los viajes a ÉL. */}
            {mejoresHoras.length > 0 ? (
              <View style={styles.zonaHoras}>
                <Text style={[styles.zonaSubtitulo, { color: colors.text }]}>Tus mejores horas</Text>
                {mejoresHoras.map((h) => (
                  <View key={h.hora} style={styles.zonaHoraFila}>
                    <Text style={[styles.zonaHoraTexto, { color: colors.text }]}>{h.rotulo}</Text>
                    <Text style={[styles.zonaHoraTexto, { color: colors.textSecondary }]}>
                      {h.n} viaje{h.n === 1 ? '' : 's'}
                    </Text>
                  </View>
                ))}
                <Text style={[styles.zonaNota, { color: colors.textSecondary }]}>
                  Sale de tus {completados.length} viaje{completados.length === 1 ? '' : 's'} completado
                  {completados.length === 1 ? '' : 's'}.
                </Text>
              </View>
            ) : (
              <View style={styles.zonaHoras}>
                <Text style={[styles.zonaSubtitulo, { color: colors.text }]}>Tus mejores horas</Text>
                <Text style={[styles.zonaNota, { color: colors.textSecondary }]}>
                  Cuando lleves unos cuantos viajes, aquí te diremos a qué horas te entran más.
                </Text>
              </View>
            )}
          </View>
        )}

        {/* QUÉ NO ESTÁ CONTADO AQUÍ — para el repartidor y el chofer escolar.
            Sin este aviso las dos cifras de arriba parecen "todo lo que has
            ganado", y no lo son: este panel solo lee la colección de viajes. */}
        {trabajoNoContado && (
          <View style={[styles.notaSinDato, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Ionicons name="information-circle-outline" size={20} color={colors.textSecondary} />
            <Text style={{ marginLeft: 10, flex: 1, fontSize: 12, color: colors.textSecondary, lineHeight: 17 }}>
              Estas cifras cuentan SOLO tus viajes de pasajeros. {trabajoNoContado.charAt(0).toUpperCase() + trabajoNoContado.slice(1)}
              {' '}no se suman en esta pantalla todavía, así que lo que ves aquí no es todo lo
              que has ganado. Si necesitas el detalle de ese trabajo, escríbenos y te lo
              damos.
            </Text>
          </View>
        )}

        {/* Los viajes cuyo precio no se pudo resolver NO se suman como cero:
            se dicen. Callarlos haría que el total pareciera completo cuando
            no lo es. */}
        {historico.sinDato > 0 && (
          <View style={[styles.notaSinDato, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Ionicons name="help-circle-outline" size={20} color={colors.textSecondary} />
            <Text style={{ marginLeft: 10, flex: 1, fontSize: 12, color: colors.textSecondary, lineHeight: 17 }}>
              {historico.sinDato === 1
                ? '1 viaje completado quedó SIN DATO de precio y no está sumado arriba.'
                : `${historico.sinDato} viajes completados quedaron SIN DATO de precio y no están sumados arriba.`}
              {' '}Preferimos dejarlos fuera antes que contarlos como RD$ 0. Si crees que
              falta dinero, escríbenos con la fecha del viaje.
            </Text>
          </View>
        )}

        {/* El ahorro solo se muestra cuando existe de verdad. Un banner que dice
            "Has ahorrado RD$ 0" a alguien que acaba de entrar no vende nada. */}
        {historico.total > 0 && (
          <View style={[styles.savingsBanner, { backgroundColor: 'rgba(0, 200, 83, 0.1)' }]}>
            <Ionicons name="trending-up" size={24} color={colors.accent} />
            <View style={{ marginLeft: 12, flex: 1 }}>
              <Text style={{ fontWeight: 'bold', color: colors.accent }}>Lo que NO pagaste en comisiones</Text>
              <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 4 }}>
                En una app que cobra 30% habrías entregado {pesos(ahorroComision)} de ese dinero.
                Aquí es tuyo completo.
              </Text>
            </View>
          </View>
        )}
        </>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1 },
  container: { padding: SIZES.padding, paddingTop: 40, paddingBottom: 100 },
  title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20 },
  sectionTitle: { fontSize: 16, fontWeight: '800', marginBottom: 12 },
  emptyState: {
    padding: 30,
    borderRadius: SIZES.radius,
    alignItems: 'center',
    borderWidth: 1,
  },
  emptyText: { fontSize: 14, textAlign: 'center' },

  orderCard: {
    padding: 14,
    borderRadius: SIZES.radius,
    marginBottom: 12,
    borderWidth: 1,
  },
  orderRest: { fontSize: 15, fontWeight: '700' },
  orderItems: { fontSize: 12, marginTop: 4 },
  orderTotal: { fontSize: 17, fontWeight: '900', marginLeft: 8 },
  orderBottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    flexWrap: 'wrap',
    gap: 8,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 10,
  },
  statusText: { fontSize: 11, fontWeight: '700', marginLeft: 4 },
  motivoCancelacion: { fontSize: 12, marginTop: 8, lineHeight: 17 },
  // Los tres puntos. El icono mide 18 px, así que el área que se puede tocar se
  // agranda con RELLENO y no con `hitSlop`: el relleno funciona en las dos
  // plataformas y no depende de una prop que en web no hace nada. Los márgenes
  // negativos devuelven ese relleno al borde de la tarjeta para no mover ni un
  // píxel de lo que ya estaba pintado.
  menuBtn: { paddingHorizontal: 12, paddingVertical: 11, marginRight: -10, marginTop: -8 },
  notaRetencion: { fontSize: 12, lineHeight: 17, marginTop: 4, marginBottom: 12 },
  filaOcultos: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: SIZES.radius,
    borderWidth: 1,
    marginBottom: 16,
  },
  trackBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    borderWidth: 1,
  },
  trackBtnText: { fontSize: 11, fontWeight: '700', marginLeft: 4 },
  reorderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: '#0066FF',
  },
  reorderBtnText: { fontSize: 11, fontWeight: '700', marginLeft: 4, color: '#FFF' },

  statsContainer: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 24 },
  // ── Mi zona de trabajo ───────────────────────────────────────────────
  zonaCaja: { padding: 16, borderRadius: SIZES.radius, borderWidth: 1, marginBottom: 24 },
  zonaCabecera: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  zonaTitulo: { fontSize: 16, fontWeight: '800' },
  zonaAyuda: { fontSize: 13, lineHeight: 19, marginBottom: 12 },
  zonaChips: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  zonaChip: { paddingVertical: 9, paddingHorizontal: 18, borderRadius: 999, borderWidth: 1 },
  zonaChipTexto: { fontSize: 14, fontWeight: '800' },
  zonaNota: { fontSize: 12, lineHeight: 17, marginTop: 4 },
  zonaAhora: { fontSize: 12, marginTop: 10, fontStyle: 'italic' },
  zonaBoton: {
    marginTop: 12,
    height: 46,
    borderRadius: 12,
    borderWidth: 1.5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  zonaBotonTexto: { fontSize: 14, fontWeight: '800' },
  zonaHoras: { marginTop: 16, paddingTop: 14, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: 'rgba(127,127,127,0.3)' },
  zonaSubtitulo: { fontSize: 14, fontWeight: '800', marginBottom: 8 },
  zonaHoraFila: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 },
  zonaHoraTexto: { fontSize: 13 },
  statCard: {
    flex: 1,
    padding: 16,
    borderRadius: SIZES.radius,
    borderWidth: 1,
    marginHorizontal: 4,
  },
  statLabel: { fontSize: 12, marginBottom: 8 },
  statValue: { fontSize: 24, fontWeight: '900' },
  statSub: { fontSize: 11, marginTop: 4 },
  notaSinDato: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 14,
    borderRadius: SIZES.radius,
    borderWidth: 1,
    marginBottom: 16,
  },
  savingsBanner: { flexDirection: 'row', alignItems: 'center', padding: 16, borderRadius: SIZES.radius },
});
